import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { TablePaginationConfig } from "antd/lib/table/interface"
import { Button, Modal, Table } from "antd"
import { winNotificationsColumns } from "./columns"
import { WinNotificationCreate } from "./winNotification.create"
import { Column } from "abstract/types/column.type"
import { DeleteIconButton, showDeleteConfirm, CreateNewElement } from "components"
import { WinNotificationsResponse, WinNotificationType } from "../store/types"
import { SendOutlined } from "@ant-design/icons"

export const WinNotificationsList: FC = observer(() => {
  const { dataStore: { winNotificationsStore } } = useStore()

  const paginationOptions: TablePaginationConfig = {
    onChange: async page => {
      winNotificationsStore.pagination.page = page
    },
    total: winNotificationsStore.total,
    showTotal: (total, range) => `${range[0]} to ${range[1]} of ${total}`,
    defaultPageSize: 10,
  }

  const [isVisibleEditCreate, setEditCreateVisibility] = useState<"create" | "edit" | null>(null)

  useEffect(() => {
    winNotificationsStore.getData()
  }, [winNotificationsStore])

  const onClose = () => {
    winNotificationsStore.clearSelectedElement()
    setEditCreateVisibility(null)
  }

  useEffect(() => {
    if (Notification.permission !== "granted") {
      Notification.requestPermission()
    }
  }, [])

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js")
    }
  }, [])

  const showNotification = (notification: WinNotificationType | undefined) => {
    if (Notification.permission === "granted" && notification) {
      navigator.serviceWorker.ready.then(registration => {
        registration.showNotification(notification.title, {
          body: notification.body,
          icon: notification?.iconImageUrl,
          image: notification?.mainImageUrl,
          tag: "buff",
          actions: notification.buttons.map(button => ({
            action: button.url,
            title: button.text,
          })),
        } as NotificationOptions & { image: string })
      })
    }
  }

  const columns: Column[] = [
    ...winNotificationsColumns,
    {
      title: "Send",
      dataIndex: "id",
      key: "id",
      align: "right",
      render: (_: any, row: WinNotificationsResponse) => (
        <Button
          type="primary"
          onClick={e => {
            e.stopPropagation()
            if (Notification.permission === "granted") {
              showNotification(row)
            }
          }}
          icon={<SendOutlined />}
        />
      ),
    },
    {
      title: "Delete",
      dataIndex: "id",
      key: "id",
      align: "right",
      render: (_: any, row: WinNotificationsResponse) => {
        return (
          <DeleteIconButton
            onClick={e => {
              e.stopPropagation()
              showDeleteConfirm(`market item: ${row}`, undefined, async() => {
                winNotificationsStore.funcAfterSuccess = async() => await winNotificationsStore.getData()

                winNotificationsStore.deleteNotificationById(row.id)
              })
            }}
          />
        )
      },
    },
  ]

  return (
    <>
      <CreateNewElement
        onClick={() => setEditCreateVisibility("create")}
        valueText="notification"
      />

      <Table
        dataSource={winNotificationsStore.notifications}
        columns={columns as any}
        rowKey="id"
        pagination={paginationOptions}
        loading={winNotificationsStore.isLoading}
        onRow={(record: WinNotificationsResponse) => ({
          onClick: () => {
            winNotificationsStore.selectedElement = record
            setEditCreateVisibility("edit")
          },
        })}
      />

      {isVisibleEditCreate === "create" && (
        <Modal
          visible
          title="Create notification"
          width="700px"
          footer={null}
          onCancel={onClose}
          style={{ top: "10px" }}
        >
          <WinNotificationCreate onClose={onClose} />
        </Modal>
      )}
      <Modal
        visible={isVisibleEditCreate === "edit"}
        footer={null}
        onCancel={onClose}
        title="Edit notification"
        width="700px"
      >
        <WinNotificationCreate data={winNotificationsStore.selectedElement} onClose={onClose} />
      </Modal>
    </>
  )
})
